import { Key, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Table } from '../../../../components/Table'
import { CheckboxCell } from '../../../../components/CheckboxCell/CheckboxCell'
import { PaymentActions } from '../../PaymentActions/PaymentActions'
import { getUser } from '../../../../redux/store/user/getters'
import { useFinancialsAbility } from '../../Providers/FinancialsAbilityProvider'
import { useFinancialsCognusARContext } from '../../Providers/FinancialsCognusARProvider'
import { useFinancialsContext } from '../../Providers/FinancialsProvider'
import { columns } from './constants'
import { getTooltipForRow } from './helpers'
import { getDisabledStatuses } from '../../utils'
import { ACTIONS, SUBJECTS } from '../../Abilities'
import { CognusARData } from './types'
import { FINANCIAL_STATUS, PAYMENT_TYPES, SelectedRowData } from '../../types'
import { ConfirmPaymentPopup } from 'features/Financials/PaymentActions/PaymentPopups/ConfirmPaymentPopup'
import { InitiatePaymentPopup } from 'features/Financials/PaymentActions/PaymentPopups/InitiatePaymentPopup'

export const CognusARTable = () => {
  const [selectedRowData, setSelectedRowData] =
    useState<SelectedRowData | null>(null)
  const [selectedMarketplaceARRowData, setSelectedMarketplaceARRowData] =
    useState<CognusARData | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const {
    state: { totalNumber, tableData, pageInfo, firstLoad, period, showHSName },
    actions: { getTableData, updateCognusARData, setPageInfo }
  } = useFinancialsCognusARContext()
  const user = useSelector(getUser)
  const ability = useFinancialsAbility()
  const {
    actions: { generateAndDownloadCognusARDocument }
  } = useFinancialsContext()
  const isVendor = !!user.vendor
  const isCommons = !!user.commons

  const canInitiateMarketplaceARPayment = useMemo(() => {
    return ability.can(ACTIONS.INITIATE, SUBJECTS.MARKETPLACE_AR_PAYMENT)
  }, [ability])

  const canCompleteMarketplaceARPayment = useMemo(() => {
    return ability.can(
      ACTIONS.INITIATE,
      SUBJECTS.COMPLETE_MARKETPLACE_AR_PAYMENT
    )
  }, [ability])

  useEffect(() => {
    getTableData({ page: pageInfo })
    setSelectedRowKeys([])
    setSelectedMarketplaceARRowData(null)
    setSelectedRowData(null)
    // eslint-disable-next-line
  }, [period])

  const handleTableChange = (pagination) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({ page })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({ page })
    setPageInfo(page)
  }

  const handleSelectRow = (
    selectedRowKeys: Key[],
    selectedRows: CognusARData[]
  ) => {
    const row = selectedRows[0] ?? null
    setSelectedRowKeys(selectedRowKeys)
    setSelectedMarketplaceARRowData(row)
    setSelectedRowData(
      row
        ? {
            uuid: row.uuid,
            health_system: row.health_system,
            supplier: row.supplier,
            total: row.total
          }
        : null
    )
  }

  const onResetSelection = () => {
    setSelectedRowData(null)
    setSelectedMarketplaceARRowData(null)
    setSelectedRowKeys([])
  }

  const updateTableData = async () => {
    try {
      await updateCognusARData()
      onResetSelection()
    } finally {
    }
  }

  return (
    <div>
      <PaymentActions
        type={PAYMENT_TYPES.MARKETPLACE_AR}
        totalNumber={totalNumber}
        pageInfo={pageInfo}
        selectedRowData={selectedRowData}
        selectedLength={selectedRowKeys.length}
        updateTableData={updateTableData}
        onResetSelection={onResetSelection}
        selectedMarketplaceARRowData={selectedMarketplaceARRowData}
      />
      {firstLoad ? null : (
        <Table<CognusARData>
          dataSource={tableData}
          columns={columns({
            isVendor,
            isCommons,
            showHSName,
            canInitiateMarketplaceARPayment,
            canCompleteMarketplaceARPayment,
            generateAndDownloadCognusARDocument
          })}
          className="financials-table"
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          rowSelection={
            canInitiateMarketplaceARPayment || canCompleteMarketplaceARPayment
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  renderCell: (_, record, __, node) => (
                    <CheckboxCell tooltipText={getTooltipForRow(record, user)}>
                      {node}
                    </CheckboxCell>
                  ),
                  getCheckboxProps: (record) => ({
                    disabled: canInitiateMarketplaceARPayment
                      ? getDisabledStatuses(
                          user.role,
                          record.due_date_status
                        ).includes(record.status)
                      : canCompleteMarketplaceARPayment
                      ? record.status !== FINANCIAL_STATUS.PARTIALLY_PAID
                      : true
                  })
                }
              : undefined
          }
        />
      )}
      <ConfirmPaymentPopup paymentType={PAYMENT_TYPES.MARKETPLACE_AR} />
      <InitiatePaymentPopup
        paymentType={PAYMENT_TYPES.MARKETPLACE_AR}
        onResetSelection={onResetSelection}
      />
    </div>
  )
}
